import {formatKm} from './simulationRules';
import {VERDICT_STYLES} from './atelierRules';

/**
 * TOUR-03 lot 6 (C-4, C-5) — règles pures de la vue « Récap » (`GET /simulations/{id}/recap`, R-TOUR-03-5 amendée).
 * AUCUN recalcul : les heures, majorations, coûts, totaux et `detail` viennent de l'API et sont seulement mis en forme.
 */

const espaces = (t) => t.replace(/[  ]/g, ' ');

/** 22.09 → « 22,09 » ; 35 → « 35 » (deux décimales au plus, virgule). */
export const nombreFr = (n) => (n == null ? '—' : espaces(Number(n).toLocaleString('fr-FR', {maximumFractionDigits: 2})));

export const eurosFr = (n) => (n == null ? '—' : `${nombreFr(n)} €`);

export const heuresFr = (n) => (n == null ? '—' : `${nombreFr(n)} h`);

const MOIS = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

/** « du 5 au 11 oct. » ; sur deux mois : « du 28 sept. au 4 oct. » (dates `YYYY-MM-DD` de l'API, sans fuseau). */
export function libellePeriode(du, au) {
    const a = /^(\d{4})-(\d{2})-(\d{2})$/.exec(du || ''), b = /^(\d{4})-(\d{2})-(\d{2})$/.exec(au || '');
    if (!a || !b) return '';
    const jourA = Number(a[3]), jourB = Number(b[3]), moisA = MOIS[Number(a[2]) - 1], moisB = MOIS[Number(b[2]) - 1];
    return a[2] === b[2] ? `du ${jourA} au ${jourB} ${moisB}` : `du ${jourA} ${moisA} au ${jourB} ${moisB}`;
}

/** « Semaine 41 · du 5 au 9 oct. · 5 journées · 1 012 km » depuis la semaine ISO `2026-W41` de l'API. */
export function titreSemaine(s) {
    const numero = /W(\d{1,2})$/.exec(s?.semaine || '')?.[1];
    const n = Number(s?.journees ?? 0);
    return espaces([
        `Semaine ${numero == null ? s?.semaine : Number(numero)}`,
        libellePeriode(s?.du, s?.au),
        `${n} ${n >= 2 ? 'journées' : 'journée'}`,
        formatKm(s?.km),
    ].filter(Boolean).join(' · '));
}

/** C-5 : le plancher hebdomadaire joue quand l'API paie plus d'heures qu'il n'y en a de travaillées. */
export const planchePaye = (o) => Number(o?.payees) > Number(o?.travaillees);

/** « plancher 35 h » (le nombre est celui des heures payées par l'API). */
export const libellePlancher = (o) => `plancher ${nombreFr(o?.payees)} h`;

/** « Équipe 1 · opérateur A » depuis l'identifiant `1-A` de l'API. */
export const libelleOperateur = (o) => `Équipe ${o?.equipe} · opérateur ${String(o?.id ?? '').split('-').pop()}`;

/** Une ligne de la table par opérateur ; `tipHeures` / `tipForfait` = les `detail` de l'API, mot pour mot. */
export function ligneOperateur(o) {
    return {
        cle: o.id,
        operateur: libelleOperateur(o),
        travaillees: heuresFr(o.travaillees),
        payees: heuresFr(o.payees),
        plancher: planchePaye(o) ? libellePlancher(o) : null,
        hn: heuresFr(o.HN),
        h25: heuresFr(o.H25),
        h50: heuresFr(o.H50),
        nuits: String(o.nuits ?? 0),
        forfait: eurosFr(o.coutForfait),
        coutHeures: eurosFr(o.coutHeures),
        tipHeures: o.detail ?? '',
        tipForfait: o.detailForfait ?? '',
    };
}

/** Une carte par semaine, dans l'ordre de l'API ; la ligne km/coût km est celle de l'équipe-semaine de l'API. */
export function cartesSemaines(recap) {
    return (recap?.semaines || []).map((s) => ({
        cle: s.semaine,
        titre: titreSemaine(s),
        operateurs: (s.operateurs || []).map(ligneOperateur),
        km: {libelle: formatKm(s.km), cout: eurosFr(s.coutKm), detail: s.detailKm ?? ''},
        trajets: s.minutesTrajets > 0 ? `Trajets base : ${nombreFr(s.minutesTrajets)} min` : null,
    }));
}

/** Bandeau verdict du récap : « 9 923 € · 5,72 €/bac · 496 €/journée · 2 481 €/semaine » (valeurs de l'API). */
export function verdictRecap(recap) {
    const t = recap?.totaux;
    if (!t) return null;
    const parties = [
        eurosFr(t.coutTotal),
        t.coutParBac != null ? `${eurosFr(t.coutParBac)}/bac` : null,
        t.coutParJournee != null ? `${eurosFr(t.coutParJournee)}/journée` : null,
        t.coutParSemaine != null ? `${eurosFr(t.coutParSemaine)}/semaine` : null,
    ].filter(Boolean);
    return {etat: 'vert', ...VERDICT_STYLES.vert, texte: espaces(parties.join(' · ')), erreur: null, signal: null};
}

/** Pied de table : totaux de campagne recopiés tels quels. */
export function totauxRecap(recap) {
    const t = recap?.totaux;
    if (!t) return null;
    return {
        travaillees: heuresFr(t.heuresTravaillees),
        payees: heuresFr(t.heuresPayees),
        hn: heuresFr(t.HN),
        h25: heuresFr(t.H25),
        h50: heuresFr(t.H50),
        nuits: String(t.nuits ?? 0),
        forfaits: `${t.forfaits ?? 0} (${eurosFr(t.coutForfaits)})`,
        coutHeures: eurosFr(t.coutHeures),
        km: formatKm(t.km),
        coutKm: eurosFr(t.coutKm),
        coutTotal: eurosFr(t.coutTotal),
    };
}

/** Bouton du 409 : « aucun coût saisi » → saisir les coûts ; calculé avant le coût RH réel → rejouer. */
export const actionRecap409 = (message) => (/aucun coût saisi/i.test(message || '') ? "Saisir les coûts dans l'atelier" : "Rejouer dans l'atelier");

/** Message de l'API (409 : pas terminé / calculé avant le coût RH réel), sinon repli. */
export const messageErreurRecap = (error) => error?.data?.detail || error?.data?.['hydra:description'] || (error?.status === 404 ? 'Scénario introuvable.' : 'Impossible de charger le récap.');
